import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { 
  DollarSign, 
  PiggyBank, 
  CreditCard, 
  Home, 
  Car, 
  GraduationCap, 
  Wallet, 
  TrendingUp,
  Building,
  Banknote,
  Calculator,
  CheckCircle,
  Save
} from 'lucide-react';
import { useCurrency } from '../contexts/CurrencyContext';

const API = process.env.REACT_APP_BACKEND_URL;

// Range options for different categories
const INCOME_RANGES = [
  { value: '0-1000', label: '$0 - $1,000' },
  { value: '1000-2500', label: '$1,000 - $2,500' },
  { value: '2500-5000', label: '$2,500 - $5,000' },
  { value: '5000-7500', label: '$5,000 - $7,500' },
  { value: '7500-10000', label: '$7,500 - $10,000' },
  { value: '10000-15000', label: '$10,000 - $15,000' },
  { value: '15000-25000', label: '$15,000 - $25,000' },
  { value: '25000-50000', label: '$25,000 - $50,000' },
  { value: '50000+', label: '$50,000+' },
];

const EXPENSE_RANGES = [
  { value: '0-1000', label: '$0 - $1,000' },
  { value: '1000-2000', label: '$1,000 - $2,000' },
  { value: '2000-3500', label: '$2,000 - $3,500' },
  { value: '3500-5000', label: '$3,500 - $5,000' },
  { value: '5000-7500', label: '$5,000 - $7,500' },
  { value: '7500-10000', label: '$7,500 - $10,000' },
  { value: '10000-15000', label: '$10,000 - $15,000' },
  { value: '15000+', label: '$15,000+' },
];

const ASSET_RANGES = [
  { value: '0-1000', label: '$0 - $1,000' },
  { value: '1000-5000', label: '$1,000 - $5,000' },
  { value: '5000-10000', label: '$5,000 - $10,000' },
  { value: '10000-25000', label: '$10,000 - $25,000' },
  { value: '25000-50000', label: '$25,000 - $50,000' },
  { value: '50000-100000', label: '$50,000 - $100,000' },
  { value: '100000-250000', label: '$100,000 - $250,000' },
  { value: '250000-500000', label: '$250,000 - $500,000' },
  { value: '500000-1000000', label: '$500,000 - $1,000,000' },
  { value: '1000000+', label: '$1,000,000+' },
];

const DEBT_RANGES = [
  { value: '0', label: '$0 (No debt)' },
  { value: '1-5000', label: '$1 - $5,000' },
  { value: '5000-10000', label: '$5,000 - $10,000' },
  { value: '10000-25000', label: '$10,000 - $25,000' },
  { value: '25000-50000', label: '$25,000 - $50,000' },
  { value: '50000-100000', label: '$50,000 - $100,000' },
  { value: '100000-250000', label: '$100,000 - $250,000' },
  { value: '250000-500000', label: '$250,000 - $500,000' },
  { value: '500000+', label: '$500,000+' },
];

const PAYMENT_RANGES = [
  { value: '0', label: '$0 (No payments)' },
  { value: '1-250', label: '$1 - $250' },
  { value: '250-500', label: '$250 - $500' },
  { value: '500-1000', label: '$500 - $1,000' },
  { value: '1000-2000', label: '$1,000 - $2,000' },
  { value: '2000-3500', label: '$2,000 - $3,500' },
  { value: '3500-5000', label: '$3,500 - $5,000' },
  { value: '5000-7500', label: '$5,000 - $7,500' },
  { value: '7500+', label: '$7,500+' },
];

const INVESTMENT_INCOME_RANGES = [
  { value: '0', label: '$0 (No investment income)' },
  { value: '1-100', label: '$1 - $100' },
  { value: '100-500', label: '$100 - $500' },
  { value: '500-1000', label: '$500 - $1,000' },
  { value: '1000-2500', label: '$1,000 - $2,500' },
  { value: '2500-5000', label: '$2,500 - $5,000' },
  { value: '5000-10000', label: '$5,000 - $10,000' },
  { value: '10000+', label: '$10,000+' },
];

const ANNUAL_BONUS_RANGES = [
  { value: '0', label: '$0 (No bonus)' },
  { value: '1-2500', label: '$1 - $2,500' },
  { value: '2500-5000', label: '$2,500 - $5,000' },
  { value: '5000-10000', label: '$5,000 - $10,000' },
  { value: '10000-25000', label: '$10,000 - $25,000' },
  { value: '25000-50000', label: '$25,000 - $50,000' },
  { value: '50000-100000', label: '$50,000 - $100,000' },
  { value: '100000-250000', label: '$100,000 - $250,000' },
  { value: '250000+', label: '$250,000+' },
];

const BONUS_FREQUENCY_OPTIONS = [
  { value: 'monthly', label: 'Monthly', description: 'Distributed every month' },
  { value: 'quarterly', label: 'Quarterly', description: 'Every 3 months (Mar, Jun, Sep, Dec)' },
  { value: 'semi-annual', label: 'Semi-Annual', description: 'Twice a year (Jun, Dec)' },
  { value: 'annual', label: 'Annual', description: 'Once a year (December)' },
];

// Helper function to format number with commas
const formatWithCommas = (value) => {
  if (value === '' || value === null || value === undefined) return '';
  const num = typeof value === 'string' ? parseFloat(value.replace(/,/g, '')) : value;
  if (isNaN(num)) return '';
  return num.toLocaleString('en-US');
};

// Helper function to parse number from formatted string
const parseFormattedNumber = (value) => {
  if (value === '' || value === null || value === undefined) return 0;
  const parsed = parseFloat(String(value).replace(/,/g, ''));
  return isNaN(parsed) ? 0 : parsed;
};

// Custom Currency Input Component with visual indicator
const CurrencyInput = ({ id, value, onChange, placeholder = "0", label }) => {
  const [displayValue, setDisplayValue] = React.useState('');
  
  // Green if value is a number (including 0), Red only if null/undefined (truly empty)
  // value === 0 should show GREEN, value === null/undefined should show RED
  const hasValue = value !== undefined && value !== null;

  React.useEffect(() => {
    if (value !== undefined && value !== null) {
      // Value exists (could be 0 or any number)
      setDisplayValue(formatWithCommas(value));
    } else {
      // Value is null or undefined - show dash
      setDisplayValue('');
    }
  }, [value]);

  const handleChange = (e) => {
    const rawValue = e.target.value;
    // Allow only numbers and commas
    const cleaned = rawValue.replace(/[^0-9,]/g, '');
    setDisplayValue(cleaned);
    
    // Parse and send numeric value
    const numericValue = parseFormattedNumber(cleaned);
    onChange(numericValue);
  };

  const handleBlur = () => {
    // Format on blur
    if (displayValue) {
      setDisplayValue(formatWithCommas(parseFormattedNumber(displayValue)));
    }
  };

  // Show green if value is a number (including 0), red only if null/undefined
  const showGreen = hasValue;

  return (
    <div className="flex items-center gap-2 mt-1">
      <span className={`inline-flex items-center justify-center w-10 h-10 rounded-full font-bold text-white flex-shrink-0 ${showGreen ? 'bg-green-600' : 'bg-red-500'}`}>
        {showGreen ? '✓' : '?'}
      </span>
      <Input
        id={id}
        type="text"
        inputMode="numeric"
        value={displayValue ? `$  ${displayValue}` : ''}
        onChange={(e) => {
          // Remove the $ prefix and spaces before processing
          const rawValue = e.target.value.replace(/^\$\s*/, '');
          const cleaned = rawValue.replace(/[^0-9,]/g, '');
          setDisplayValue(cleaned);
          const numericValue = parseFormattedNumber(cleaned);
          onChange(numericValue);
        }}
        onBlur={handleBlur}
        className="flex-1"
        placeholder={hasValue ? "$  0" : "-"}
      />
    </div>
  );
};

const MyNumbersModule = () => {
  const { t } = useTranslation();
  const { formatAmount, activeCurrency } = useCurrency();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [inputMode, setInputMode] = useState('accurate'); // 'accurate' or 'range'
  const [hasData, setHasData] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [activeSection, setActiveSection] = useState('income-expenses'); // 'income-expenses' or 'assets-debt'
  
  // Form state - Initialize with null for fields that haven't been set yet
  const [formData, setFormData] = useState({
    currency: 'USD',
    // Income
    monthly_income: null,
    monthly_income_range: '',
    annual_bonus: null,
    annual_bonus_range: '',
    bonus_frequency: 'monthly', // Default to monthly distribution
    // Expenses
    monthly_expenses: null,
    monthly_expenses_range: '',
    // Expense allocation percentages
    expense_allocation: {
      basic_survival: null,
      normal_lifestyle: null,
      luxury_emotional: null,
    },
    // Assets breakdown - Productive (generate returns)
    productive_assets: {
      investments: null,
      retirement_accounts: null,
      rental_properties: null,
      bank_cds: null,
      other_productive: null,
    },
    // Assets breakdown - Non-productive (no returns)
    non_productive_assets: {
      savings: null,
      checking: null,
      cash: null,
      emergency_fund: null,
      primary_residence: null,
      vehicles: null,
      personal_property: null,
      other_non_productive: null,
    },
    total_productive_assets: null,
    total_non_productive_assets: null,
    total_assets: null,
    total_assets_range: '',
    // Debts breakdown
    debts: {
      credit_cards: null,
      mortgage: null,
      car_loans: null,
      student_loans: null,
      personal_loans: null,
      other_debt: null,
    },
    total_debt: null,
    total_debt_range: '',
    // Monthly payments
    monthly_payments: {
      credit_card_payments: null,
      mortgage_payments: null,
      car_loan_payments: null,
      student_loan_payments: null,
      personal_loan_payments: null,
      other_debt_payments: null,
    },
    total_monthly_debt_payments: null,
    total_monthly_debt_payments_range: '',
    // Investment income
    monthly_investment_income: null,
    monthly_investment_income_range: '',
  });

  // Get auth headers
  const getAuthHeaders = () => {
    const token = sessionStorage.getItem('prosperMindToken') || sessionStorage.getItem('authToken');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  // Load existing data
  useEffect(() => {
    fetchMyNumbers();
  }, []);

  const fetchMyNumbers = async () => {
    try {
      const response = await axios.get(`${API}/api/my-numbers`, {
        headers: getAuthHeaders()
      });
      
      if (response.data) {
        setFormData(prev => ({
          ...prev,
          ...response.data,
          productive_assets: response.data.productive_assets || prev.productive_assets,
          non_productive_assets: response.data.non_productive_assets || prev.non_productive_assets,
          debts: response.data.debts || prev.debts,
          monthly_payments: response.data.monthly_payments || prev.monthly_payments,
          expense_allocation: response.data.expense_allocation || prev.expense_allocation,
        }));
        setInputMode(response.data.input_mode || 'accurate');
        setHasData(true);
        // Set last updated timestamp
        if (response.data.updated_at) {
          setLastUpdated(response.data.updated_at);
        }
      }
    } catch (error) {
      console.error('Error fetching my numbers:', error);
    } finally {
      setLoading(false);
    }
  };

  // Calculate totals - use useMemo instead of useEffect to avoid state loops
  const calculatedTotals = useMemo(() => {
    if (inputMode === 'accurate') {
      const totalProductiveAssets = Object.values(formData.productive_assets).reduce((sum, val) => sum + (parseFloat(val) || 0), 0);
      const totalNonProductiveAssets = Object.values(formData.non_productive_assets).reduce((sum, val) => sum + (parseFloat(val) || 0), 0);
      const totalAssets = totalProductiveAssets + totalNonProductiveAssets;
      const totalDebt = Object.values(formData.debts).reduce((sum, val) => sum + (parseFloat(val) || 0), 0);
      const totalPayments = Object.values(formData.monthly_payments).reduce((sum, val) => sum + (parseFloat(val) || 0), 0);
      return { totalProductiveAssets, totalNonProductiveAssets, totalAssets, totalDebt, totalPayments };
    }
    return { totalProductiveAssets: 0, totalNonProductiveAssets: 0, totalAssets: 0, totalDebt: 0, totalPayments: 0 };
  }, [inputMode, formData.productive_assets, formData.non_productive_assets, formData.debts, formData.monthly_payments]);

  // Handle input change
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle nested input change (assets, debts, payments)
  const handleNestedChange = (category, field, value) => {
    setFormData(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [field]: parseFloat(value) || 0
      }
    }));
  };

  // Save data
  const handleSave = async () => {
    setSaving(true);
    try {
      // Include calculated totals in the save data
      const dataToSave = {
        ...formData,
        input_mode: inputMode,
        total_productive_assets: calculatedTotals.totalProductiveAssets,
        total_non_productive_assets: calculatedTotals.totalNonProductiveAssets,
        total_assets: calculatedTotals.totalAssets,
        total_debt: calculatedTotals.totalDebt,
        total_monthly_debt_payments: calculatedTotals.totalPayments,
      };
      
      const response = await axios.post(`${API}/api/my-numbers`, dataToSave, {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        }
      });
      
      toast.success(t('myNumbers.saveSuccess') || 'Financial information saved successfully!');
      setHasData(true);
      
      // Update the last updated timestamp
      if (response.data?.updated_at) {
        setLastUpdated(response.data.updated_at);
      } else {
        setLastUpdated(new Date().toISOString());
      }
      
      // Dispatch custom event to notify other modules to refresh their data
      window.dispatchEvent(new CustomEvent('myNumbersUpdated', { 
        detail: { 
          ...dataToSave,
          timestamp: Date.now() 
        } 
      }));
      
    } catch (error) {
      console.error('Error saving my numbers:', error);
      toast.error(t('myNumbers.saveError') || 'Failed to save financial information');
    } finally {
      setSaving(false);
    }
  };

  // Format currency for display
  const formatValue = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: formData.currency || 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value || 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="my-numbers-module">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-white" data-testid="my-numbers-title">
            {t('myNumbers.title') || 'My Numbers'}
          </h2>
          <p className="text-gray-400 mt-1">
            {t('myNumbers.description') || 'Provide your financial information to get personalized insights'}
          </p>
          {/* Last Updated Timestamp */}
          {lastUpdated && (
            <p className="text-sm text-emerald-400 mt-2 flex items-center gap-1" data-testid="last-updated">
              <CheckCircle className="w-4 h-4" />
              Last updated: {new Date(lastUpdated).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </p>
          )}
        </div>
        
        {/* Input Mode Toggle */}
        <div className="flex items-center gap-2 bg-gray-100 p-1 rounded-lg" data-testid="input-mode-toggle">
          <Button
            variant={inputMode === 'accurate' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setInputMode('accurate')}
            className={inputMode === 'accurate' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
            data-testid="accurate-mode-btn"
          >
            <Calculator className="w-4 h-4 mr-2" />
            {t('myNumbers.accurate') || 'Accurate'}
          </Button>
          <Button
            variant={inputMode === 'range' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setInputMode('range')}
            className={inputMode === 'range' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
            data-testid="ranges-mode-btn"
          >
            <TrendingUp className="w-4 h-4 mr-2" />
            {t('myNumbers.ranges') || 'Ranges'}
          </Button>
        </div>
      </div>

      {/* Section Tabs */}
      <div className="flex items-center gap-2 bg-gray-800 p-1 rounded-lg w-fit" data-testid="section-tabs">
        <Button
          variant={activeSection === 'income-expenses' ? 'default' : 'ghost'}
          size="sm"
          onClick={() => setActiveSection('income-expenses')}
          className={activeSection === 'income-expenses' ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : 'text-gray-300 hover:text-white hover:bg-gray-700'}
          data-testid="income-expenses-tab"
        >
          <TrendingUp className="w-4 h-4 mr-2" />
          Income, Expenses & Payments
        </Button>
        <Button
          variant={activeSection === 'assets-debt' ? 'default' : 'ghost'}
          size="sm"
          onClick={() => setActiveSection('assets-debt')}
          className={activeSection === 'assets-debt' ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : 'text-gray-300 hover:text-white hover:bg-gray-700'}
          data-testid="assets-debt-tab"
        >
          <Building className="w-4 h-4 mr-2" />
          Assets & Debt
        </Button>
      </div>

      {/* Income & Expenses Section */}
      {activeSection === 'income-expenses' && (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Income Section */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <TrendingUp className="w-5 h-5 text-green-600" />
              {t('myNumbers.income') || 'Income'}
            </CardTitle>
            <CardDescription>{t('myNumbers.incomeDescription') || 'Your monthly income from all sources'}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {inputMode === 'accurate' ? (
              <div>
                <Label htmlFor="monthly_income">{t('myNumbers.monthlyIncome') || 'Monthly Income'}</Label>
                <CurrencyInput
                  id="monthly_income"
                  value={formData.monthly_income}
                  onChange={(value) => handleInputChange('monthly_income', value)}
                  placeholder="0"
                />
              </div>
            ) : (
              <div>
                <Label>{t('myNumbers.monthlyIncomeRange') || 'Monthly Income Range'}</Label>
                <Select
                  value={formData.monthly_income_range}
                  onValueChange={(value) => handleInputChange('monthly_income_range', value)}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select range" />
                  </SelectTrigger>
                  <SelectContent>
                    {INCOME_RANGES.map((range) => (
                      <SelectItem key={range.value} value={range.value}>
                        {range.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Investment Income */}
            <div className="pt-2 border-t">
              <Label className="text-sm text-gray-600">
                {t('myNumbers.investmentIncome') || 'Monthly Investment Income (dividends, interest, etc.)'}
              </Label>
              {inputMode === 'accurate' ? (
                <CurrencyInput
                  id="monthly_investment_income"
                  value={formData.monthly_investment_income}
                  onChange={(value) => handleInputChange('monthly_investment_income', value)}
                  placeholder="0"
                />
              ) : (
                <Select
                  value={formData.monthly_investment_income_range}
                  onValueChange={(value) => handleInputChange('monthly_investment_income_range', value)}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select range" />
                  </SelectTrigger>
                  <SelectContent>
                    {INVESTMENT_INCOME_RANGES.map((range) => (
                      <SelectItem key={range.value} value={range.value}>
                        {range.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            {/* Annual Bonus */}
            <div className="pt-2 border-t">
              <Label className="text-sm text-gray-600">
                Annual Bonus (yearly bonuses, commissions, etc.)
              </Label>
              {inputMode === 'accurate' ? (
                <CurrencyInput
                  id="annual_bonus"
                  value={formData.annual_bonus}
                  onChange={(value) => handleInputChange('annual_bonus', value)}
                  placeholder="0"
                />
              ) : (
                <Select
                  value={formData.annual_bonus_range}
                  onValueChange={(value) => handleInputChange('annual_bonus_range', value)}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select range" />
                  </SelectTrigger>
                  <SelectContent>
                    {ANNUAL_BONUS_RANGES.map((range) => (
                      <SelectItem key={range.value} value={range.value}>
                        {range.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              
              {/* Bonus Frequency Selector */}
              {(formData.annual_bonus > 0 || formData.annual_bonus_range) && (
                <div className="mt-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
                  <Label className="text-sm text-amber-800 font-medium mb-2 block">
                    Bonus Payout Frequency
                  </Label>
                  <div className="grid grid-cols-2 gap-2">
                    {BONUS_FREQUENCY_OPTIONS.map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => handleInputChange('bonus_frequency', option.value)}
                        className={`p-2 rounded-lg text-left transition-all ${
                          formData.bonus_frequency === option.value
                            ? 'bg-amber-500 text-white shadow-md'
                            : 'bg-white text-gray-700 border border-gray-200 hover:border-amber-300'
                        }`}
                      >
                        <div className="font-medium text-sm">{option.label}</div>
                        <div className={`text-xs ${formData.bonus_frequency === option.value ? 'text-amber-100' : 'text-gray-500'}`}>
                          {option.description}
                        </div>
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-amber-700 mt-2">
                    {formData.bonus_frequency === 'monthly' && `${formatAmount((formData.annual_bonus || 0) / 12)}/month distributed evenly`}
                    {formData.bonus_frequency === 'quarterly' && `${formatAmount((formData.annual_bonus || 0) / 4)} paid 4x/year`}
                    {formData.bonus_frequency === 'semi-annual' && `${formatAmount((formData.annual_bonus || 0) / 2)} paid 2x/year`}
                    {formData.bonus_frequency === 'annual' && `${formatAmount(formData.annual_bonus || 0)} paid once in December`}
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Expenses Section */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Banknote className="w-5 h-5 text-orange-600" />
              {t('myNumbers.expenses') || 'Expenses'}
            </CardTitle>
            <CardDescription>{t('myNumbers.expensesDescription') || 'Your total monthly expenses'}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {inputMode === 'accurate' ? (
              <div>
                <Label htmlFor="monthly_expenses">{t('myNumbers.monthlyExpenses') || 'Monthly Expenses'}</Label>
                <CurrencyInput
                  id="monthly_expenses"
                  value={formData.monthly_expenses}
                  onChange={(value) => handleInputChange('monthly_expenses', value)}
                  placeholder="0"
                />
              </div>
            ) : (
              <div>
                <Label>{t('myNumbers.monthlyExpensesRange') || 'Monthly Expenses Range'}</Label>
                <Select
                  value={formData.monthly_expenses_range}
                  onValueChange={(value) => handleInputChange('monthly_expenses_range', value)}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select range" />
                  </SelectTrigger>
                  <SelectContent>
                    {EXPENSE_RANGES.map((range) => (
                      <SelectItem key={range.value} value={range.value}>
                        {range.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            {/* Expense Allocation - Only show when monthly expenses has been entered */}
            {formData.monthly_expenses != null && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <Label className="text-sm font-medium text-gray-700 mb-3 block">
                Expense Allocation (% of total)
              </Label>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs text-gray-500">Basic / Survival</Label>
                  <div className="flex items-center mt-1">
                    <input
                      type="text"
                      inputMode="numeric"
                      value={formData.expense_allocation?.basic_survival != null ? `${formData.expense_allocation.basic_survival} %` : ''}
                      onChange={(e) => {
                        const rawValue = e.target.value.replace(/[^0-9]/g, '');
                        const value = rawValue === '' ? null : Math.min(100, parseInt(rawValue));
                        handleNestedChange('expense_allocation', 'basic_survival', value);
                        // Auto-calculate third value if first two are set
                        const normalVal = formData.expense_allocation?.normal_lifestyle;
                        if (value != null && normalVal != null) {
                          const luxuryVal = Math.max(0, 100 - value - normalVal);
                          handleNestedChange('expense_allocation', 'luxury_emotional', luxuryVal);
                        }
                      }}
                      className="w-full text-center py-2 px-3 rounded-md border-0 bg-green-600 text-white font-medium placeholder-green-200"
                      placeholder="- %"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Normal / Lifestyle</Label>
                  <div className="flex items-center mt-1">
                    <input
                      type="text"
                      inputMode="numeric"
                      value={formData.expense_allocation?.normal_lifestyle != null ? `${formData.expense_allocation.normal_lifestyle} %` : ''}
                      onChange={(e) => {
                        const rawValue = e.target.value.replace(/[^0-9]/g, '');
                        const value = rawValue === '' ? null : Math.min(100, parseInt(rawValue));
                        handleNestedChange('expense_allocation', 'normal_lifestyle', value);
                        // Auto-calculate third value if first two are set
                        const basicVal = formData.expense_allocation?.basic_survival;
                        if (value != null && basicVal != null) {
                          const luxuryVal = Math.max(0, 100 - basicVal - value);
                          handleNestedChange('expense_allocation', 'luxury_emotional', luxuryVal);
                        }
                      }}
                      className="w-full text-center py-2 px-3 rounded-md border-0 bg-yellow-500 text-white font-medium placeholder-yellow-200"
                      placeholder="- %"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Luxury / Emotional</Label>
                  <div className="flex items-center mt-1">
                    <input
                      type="text"
                      inputMode="numeric"
                      value={formData.expense_allocation?.luxury_emotional != null ? `${formData.expense_allocation.luxury_emotional} %` : ''}
                      onChange={(e) => {
                        const rawValue = e.target.value.replace(/[^0-9]/g, '');
                        const value = rawValue === '' ? null : Math.min(100, parseInt(rawValue));
                        handleNestedChange('expense_allocation', 'luxury_emotional', value);
                      }}
                      className="w-full text-center py-2 px-3 rounded-md border-0 bg-red-500 text-white font-medium placeholder-red-200"
                      placeholder="- %"
                    />
                  </div>
                </div>
              </div>
              {/* Total validation */}
              {(() => {
                const total = (formData.expense_allocation?.basic_survival || 0) + 
                              (formData.expense_allocation?.normal_lifestyle || 0) + 
                              (formData.expense_allocation?.luxury_emotional || 0);
                const hasAnyValue = formData.expense_allocation?.basic_survival != null || 
                                    formData.expense_allocation?.normal_lifestyle != null || 
                                    formData.expense_allocation?.luxury_emotional != null;
                if (hasAnyValue && total !== 100) {
                  return (
                    <p className={`text-xs mt-2 ${total > 100 ? 'text-red-500' : 'text-orange-500'}`}>
                      Total: {total}% {total !== 100 && `(should equal 100%)`}
                    </p>
                  );
                } else if (hasAnyValue && total === 100) {
                  return (
                    <p className="text-xs mt-2 text-green-600">
                      Total: 100% ✓
                    </p>
                  );
                }
                return null;
              })()}
            </div>
            )}
          </CardContent>
        </Card>

        {/* Monthly Debt Payments Section */}
        <Card className="lg:col-span-2">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Wallet className="w-5 h-5 text-purple-600" />
              {t('myNumbers.monthlyDebtPayments') || 'Monthly Debt Payments'}
            </CardTitle>
            <CardDescription>{t('myNumbers.monthlyPaymentsDescription') || 'How much you pay towards debts each month'}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {inputMode === 'accurate' ? (
              <>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  <div>
                    <Label className="text-sm">{t('myNumbers.creditCardPayments') || 'Credit Card Payments'}</Label>
                    <CurrencyInput
                      id="payments_credit_card"
                      value={formData.monthly_payments.credit_card_payments}
                      onChange={(value) => handleNestedChange('monthly_payments', 'credit_card_payments', value)}
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <Label className="text-sm">{t('myNumbers.mortgagePayments') || 'Mortgage Payments'}</Label>
                    <CurrencyInput
                      id="payments_mortgage"
                      value={formData.monthly_payments.mortgage_payments}
                      onChange={(value) => handleNestedChange('monthly_payments', 'mortgage_payments', value)}
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <Label className="text-sm">{t('myNumbers.carLoanPayments') || 'Car Loan Payments'}</Label>
                    <CurrencyInput
                      id="payments_car_loan"
                      value={formData.monthly_payments.car_loan_payments}
                      onChange={(value) => handleNestedChange('monthly_payments', 'car_loan_payments', value)}
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <Label className="text-sm">{t('myNumbers.studentLoanPayments') || 'Student Loan Payments'}</Label>
                    <CurrencyInput
                      id="payments_student_loan"
                      value={formData.monthly_payments.student_loan_payments}
                      onChange={(value) => handleNestedChange('monthly_payments', 'student_loan_payments', value)}
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <Label className="text-sm">{t('myNumbers.personalLoanPayments') || 'Personal Loan Payments'}</Label>
                    <CurrencyInput
                      id="payments_personal_loan"
                      value={formData.monthly_payments.personal_loan_payments}
                      onChange={(value) => handleNestedChange('monthly_payments', 'personal_loan_payments', value)}
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <Label className="text-sm">{t('myNumbers.otherDebtPayments') || 'Other Payments'}</Label>
                    <CurrencyInput
                      id="payments_other"
                      value={formData.monthly_payments.other_debt_payments}
                      onChange={(value) => handleNestedChange('monthly_payments', 'other_debt_payments', value)}
                      placeholder="0"
                    />
                  </div>
                </div>
                <div className="pt-3 border-t">
                  <div className="flex justify-between items-center">
                    <span className="font-medium text-gray-700">{t('myNumbers.totalMonthlyPayments') || 'Total Monthly Debt Payments'}</span>
                    <span className="text-xl font-bold text-purple-600">{formatValue(calculatedTotals.totalPayments)}</span>
                  </div>
                </div>
              </>
            ) : (
              <div>
                <Label>{t('myNumbers.totalMonthlyPaymentsRange') || 'Total Monthly Debt Payments Range'}</Label>
                <Select
                  value={formData.total_monthly_debt_payments_range}
                  onValueChange={(value) => handleInputChange('total_monthly_debt_payments_range', value)}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select range" />
                  </SelectTrigger>
                  <SelectContent>
                    {PAYMENT_RANGES.map((range) => (
                      <SelectItem key={range.value} value={range.value}>
                        {range.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      )}

      {/* Assets & Debt Section */}
      {activeSection === 'assets-debt' && (
      <div className="space-y-6">
        {/* Assets Grid - 2 columns */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Productive Assets Section */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                <TrendingUp className="w-5 h-5 text-emerald-600" />
                Productive Assets
              </CardTitle>
              <CardDescription>Assets that generate returns (investments, rental income, dividends)</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {inputMode === 'accurate' ? (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-sm">Investments (Stocks, ETFs, Bonds, Dividend Stocks)</Label>
                      <CurrencyInput
                        id="productive_investments"
                        value={formData.productive_assets.investments}
                        onChange={(value) => handleNestedChange('productive_assets', 'investments', value)}
                        placeholder="0"
                      />
                    </div>
                    <div>
                      <Label className="text-sm">Retirement (401k, IRA)</Label>
                      <CurrencyInput
                        id="productive_retirement"
                        value={formData.productive_assets.retirement_accounts}
                        onChange={(value) => handleNestedChange('productive_assets', 'retirement_accounts', value)}
                        placeholder="0"
                      />
                    </div>
                    <div>
                      <Label className="text-sm">Rental Properties</Label>
                      <CurrencyInput
                        id="productive_rental"
                        value={formData.productive_assets.rental_properties}
                        onChange={(value) => handleNestedChange('productive_assets', 'rental_properties', value)}
                        placeholder="0"
                      />
                    </div>
                    <div>
                      <Label className="text-sm">Bank CDs</Label>
                      <CurrencyInput
                        id="productive_cds"
                        value={formData.productive_assets.bank_cds}
                        onChange={(value) => handleNestedChange('productive_assets', 'bank_cds', value)}
                        placeholder="0"
                      />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-sm">Other Productive (Private businesses, licenses, royalties, etc.)</Label>
                      <CurrencyInput
                        id="productive_other"
                        value={formData.productive_assets.other_productive}
                        onChange={(value) => handleNestedChange('productive_assets', 'other_productive', value)}
                        placeholder="0"
                      />
                    </div>
                  </div>
                  <div className="pt-3 border-t bg-emerald-50 -mx-4 px-4 py-2 rounded-b-lg">
                    <div className="flex justify-between items-center">
                      <span className="font-medium text-gray-700">Total Productive Assets</span>
                      <span className="text-xl font-bold text-emerald-600">{formatValue(calculatedTotals.totalProductiveAssets)}</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Used for passive income calculations</p>
                  </div>
                </>
              ) : (
                <div>
                  <Label>Total Productive Assets Range</Label>
                  <Select
                    value={formData.total_assets_range}
                    onValueChange={(value) => handleInputChange('total_assets_range', value)}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select range" />
                    </SelectTrigger>
                    <SelectContent>
                      {ASSET_RANGES.map(range => (
                        <SelectItem key={range.value} value={range.value}>{range.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Non-Productive Assets Section */}
          <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Home className="w-5 h-5 text-blue-600" />
              Non-Productive Assets
            </CardTitle>
            <CardDescription>Assets that don't generate returns (personal use items)</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {inputMode === 'accurate' ? (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-sm">Savings Account</Label>
                    <CurrencyInput
                      id="non_productive_savings"
                      value={formData.non_productive_assets.savings}
                      onChange={(value) => handleNestedChange('non_productive_assets', 'savings', value)}
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <Label className="text-sm">Checking Account</Label>
                    <CurrencyInput
                      id="non_productive_checking"
                      value={formData.non_productive_assets.checking}
                      onChange={(value) => handleNestedChange('non_productive_assets', 'checking', value)}
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <Label className="text-sm">Cash</Label>
                    <CurrencyInput
                      id="non_productive_cash"
                      value={formData.non_productive_assets.cash}
                      onChange={(value) => handleNestedChange('non_productive_assets', 'cash', value)}
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <Label className="text-sm">Emergency Fund</Label>
                    <CurrencyInput
                      id="non_productive_emergency"
                      value={formData.non_productive_assets.emergency_fund}
                      onChange={(value) => handleNestedChange('non_productive_assets', 'emergency_fund', value)}
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <Label className="text-sm">Primary Residence</Label>
                    <CurrencyInput
                      id="non_productive_residence"
                      value={formData.non_productive_assets.primary_residence}
                      onChange={(value) => handleNestedChange('non_productive_assets', 'primary_residence', value)}
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <Label className="text-sm">Vehicles</Label>
                    <CurrencyInput
                      id="non_productive_vehicles"
                      value={formData.non_productive_assets.vehicles}
                      onChange={(value) => handleNestedChange('non_productive_assets', 'vehicles', value)}
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <Label className="text-sm">Personal Property</Label>
                    <CurrencyInput
                      id="non_productive_personal"
                      value={formData.non_productive_assets.personal_property}
                      onChange={(value) => handleNestedChange('non_productive_assets', 'personal_property', value)}
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <Label className="text-sm">Other Non-Productive</Label>
                    <CurrencyInput
                      id="non_productive_other"
                      value={formData.non_productive_assets.other_non_productive}
                      onChange={(value) => handleNestedChange('non_productive_assets', 'other_non_productive', value)}
                      placeholder="0"
                    />
                  </div>
                </div>
                <div className="pt-3 border-t bg-blue-50 -mx-4 px-4 py-2">
                  <div className="flex justify-between items-center">
                    <span className="font-medium text-gray-700">Total Non-Productive Assets</span>
                    <span className="text-xl font-bold text-blue-600">{formatValue(calculatedTotals.totalNonProductiveAssets)}</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Used for net worth but not passive income calculations</p>
                </div>
              </>
            ) : (
              <div>
                <Label>Total Assets Range</Label>
                <Select
                  value={formData.total_assets_range}
                  onValueChange={(value) => handleInputChange('total_assets_range', value)}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select range" />
                  </SelectTrigger>
                  <SelectContent>
                    {ASSET_RANGES.map(range => (
                      <SelectItem key={range.value} value={range.value}>{range.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </CardContent>
        </Card>
        </div>

        {/* Combined Total */}
        {inputMode === 'accurate' && (
          <Card className="bg-gradient-to-r from-emerald-50 to-blue-50">
            <CardContent className="py-4">
              <div className="flex justify-between items-center">
                <div>
                  <span className="font-medium text-gray-700">Combined Total Assets</span>
                  <p className="text-xs text-gray-500">Productive + Non-Productive</p>
                </div>
                <span className="text-2xl font-bold text-gray-800">{formatValue(calculatedTotals.totalAssets)}</span>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Debts Grid - 2 columns */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Debts Section */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <CreditCard className="w-5 h-5 text-red-600" />
              {t('myNumbers.debts') || 'Debts'}
            </CardTitle>
            <CardDescription>{t('myNumbers.debtsDescription') || 'All outstanding debts and loans'}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {inputMode === 'accurate' ? (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-sm">{t('myNumbers.creditCards') || 'Credit Cards'}</Label>
                    <CurrencyInput
                      id="debts_credit_cards"
                      value={formData.debts.credit_cards}
                      onChange={(value) => handleNestedChange('debts', 'credit_cards', value)}
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <Label className="text-sm">{t('myNumbers.mortgage') || 'Mortgage'}</Label>
                    <CurrencyInput
                      id="debts_mortgage"
                      value={formData.debts.mortgage}
                      onChange={(value) => handleNestedChange('debts', 'mortgage', value)}
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <Label className="text-sm">{t('myNumbers.carLoans') || 'Car Loans'}</Label>
                    <CurrencyInput
                      id="debts_car_loans"
                      value={formData.debts.car_loans}
                      onChange={(value) => handleNestedChange('debts', 'car_loans', value)}
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <Label className="text-sm">{t('myNumbers.studentLoans') || 'Student Loans'}</Label>
                    <CurrencyInput
                      id="debts_student_loans"
                      value={formData.debts.student_loans}
                      onChange={(value) => handleNestedChange('debts', 'student_loans', value)}
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <Label className="text-sm">{t('myNumbers.personalLoans') || 'Personal Loans'}</Label>
                    <CurrencyInput
                      id="debts_personal_loans"
                      value={formData.debts.personal_loans}
                      onChange={(value) => handleNestedChange('debts', 'personal_loans', value)}
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <Label className="text-sm">{t('myNumbers.otherDebt') || 'Other Debt'}</Label>
                    <CurrencyInput
                      id="debts_other"
                      value={formData.debts.other_debt}
                      onChange={(value) => handleNestedChange('debts', 'other_debt', value)}
                      placeholder="0"
                    />
                  </div>
                </div>
                <div className="pt-3 border-t">
                  <div className="flex justify-between items-center">
                    <span className="font-medium text-gray-700">{t('myNumbers.totalDebt') || 'Total Debt'}</span>
                    <span className="text-xl font-bold text-red-600">{formatValue(calculatedTotals.totalDebt)}</span>
                  </div>
                </div>
              </>
            ) : (
              <div>
                <Label>{t('myNumbers.totalDebtRange') || 'Total Debt Range'}</Label>
                <Select
                  value={formData.total_debt_range}
                  onValueChange={(value) => handleInputChange('total_debt_range', value)}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select range" />
                  </SelectTrigger>
                  <SelectContent>
                    {DEBT_RANGES.map((range) => (
                      <SelectItem key={range.value} value={range.value}>
                        {range.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </CardContent>
        </Card>
        </div>
      </div>
      )}

      {/* Save Button */}
      <div className="flex justify-end pt-4">
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-emerald-600 hover:bg-emerald-700 min-w-[200px]"
          size="lg"
          data-testid="save-my-numbers-btn"
        >
          {saving ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              {t('myNumbers.saving') || 'Saving...'}
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              {t('myNumbers.saveNumbers') || 'Save My Numbers'}
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default MyNumbersModule;
